import { useEffect } from "react";
import { X, ShieldCheck } from "lucide-react";
import { DisclaimerContent } from "./DisclaimerContent";

// Floating, no-signup-required disclaimer modal. Used on public surfaces
// (pricing page FAQ, etc.) where we want to expose the disclaimer text
// without sending the visitor through auth or the checkbox-gate.

export const DisclaimerModal = ({ open, onClose }) => {
  // Lock scroll on the underlying page while the modal is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Disclaimer"
      data-testid="disclaimer-modal"
    >
      {/* Backdrop — tap to close */}
      <button
        onClick={onClose}
        className="absolute inset-0 bg-[#1B2349]/55 backdrop-blur-sm cursor-default"
        aria-label="Close disclaimer"
        data-testid="disclaimer-modal-backdrop"
      />

      {/* Modal panel */}
      <div
        className="relative bg-[#FBF8F1] rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col shadow-[0_24px_60px_rgba(0,0,0,0.25)]"
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Top accent strip */}
        <div className="h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887]" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 sm:px-8 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#EAF0F9] rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#78C0D8]" />
            </div>
            <h2 className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349]">
              Important Disclaimer
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-[#4A5568]"
            aria-label="Close"
            data-testid="disclaimer-modal-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 pb-8">
          <DisclaimerContent />
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
